import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useAdminAuth } from "@/components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GripVertical, Save, Eye, CheckCircle, XCircle, Loader2, CreditCard } from "lucide-react";

export default function AdminPricing() {
  const { admin } = useAdminAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
  });

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API}/admin/pricing`, { headers: getAuthHeaders() });
      setPlans(res.data.plans || []);
    } catch (err) {
      toast.error("Failed to load pricing plans");
    } finally {
      setLoading(false);
    }
  };

  const savePlan = async (planKey, updates) => {
    setSaving(planKey);
    try {
      await axios.put(`${API}/admin/pricing/${planKey}`, updates, { headers: getAuthHeaders() });
      toast.success(`Updated ${planKey}`);
      await fetchPlans();
      setEditingPlan(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-pricing">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Pricing Control</h1>
          <p className="text-muted-foreground text-sm">Manage plan pricing, features, Stripe wiring, and display settings. Changes take effect immediately.</p>
        </div>
        <a href="/pricing" target="_blank" rel="noopener noreferrer">
          <Button variant="outline" size="sm" className="gap-2" data-testid="preview-pricing-btn">
            <Eye className="w-4 h-4" /> Preview
          </Button>
        </a>
      </div>

      <Founding40Panel authHeaders={getAuthHeaders()} />

      <div className="space-y-4">
        {plans.map((plan) => (
          <PlanEditor
            key={plan.plan_key}
            plan={plan}
            isEditing={editingPlan === plan.plan_key}
            onEdit={() => setEditingPlan(editingPlan === plan.plan_key ? null : plan.plan_key)}
            onSave={(updates) => savePlan(plan.plan_key, updates)}
            saving={saving === plan.plan_key}
            authHeaders={getAuthHeaders()}
          />
        ))}
      </div>
    </div>
  );
}

function Founding40Panel({ authHeaders }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/pricing/founding40`, { headers: authHeaders });
      setData(res.data);
    } catch (err) {
      console.error("Failed to load FOUNDING40 status", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleToggle = async (active) => {
    setToggling(true);
    try {
      await axios.post(`${API}/admin/pricing/founding40/toggle`, { is_active: active }, { headers: authHeaders });
      toast.success(`FOUNDING40 ${active ? "activated" : "deactivated"}`);
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Toggle failed");
    } finally {
      setToggling(false);
    }
  };

  if (loading || !data) return null;

  return (
    <Card data-testid="founding40-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              FOUNDING40 — Founding Customer Program
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium ${data.is_active ? "bg-emerald-100 text-emerald-800" : "bg-zinc-200 text-zinc-600"}`}
                data-testid="founding40-status"
              >
                {data.is_active ? "Active" : "Inactive"}
              </span>
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              {data.percent_off}% off for {data.duration_months} months · Starter & Pro · monthly and annual
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Code active</Label>
            <Switch
              checked={data.is_active}
              onCheckedChange={handleToggle}
              disabled={toggling}
              data-testid="founding40-toggle"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-6 text-sm">
          <div data-testid="founding40-redemptions">
            <span className="font-semibold text-foreground">{data.redemptions_used}</span>
            <span className="text-muted-foreground"> / {data.max_redemptions} redemptions used</span>
          </div>
          {data.stripe_coupon_id && (
            <div className="text-xs text-muted-foreground">
              Stripe coupon: <code className="bg-muted px-1 rounded">{data.stripe_coupon_id}</code>
            </div>
          )}
        </div>
        {data.redeemers.length > 0 ? (
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted/50">
                <tr className="text-left text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Account</th>
                  <th className="px-3 py-2 font-medium">Plan</th>
                  <th className="px-3 py-2 font-medium">Billing</th>
                  <th className="px-3 py-2 font-medium">Paid</th>
                  <th className="px-3 py-2 font-medium">Redeemed</th>
                </tr>
              </thead>
              <tbody>
                {data.redeemers.map((r, i) => (
                  <tr key={i} className="border-t border-border" data-testid="founding40-redeemer-row">
                    <td className="px-3 py-2 text-foreground">{r.user_email || "—"}</td>
                    <td className="px-3 py-2 capitalize">{r.plan_key || "—"}</td>
                    <td className="px-3 py-2 capitalize">{r.billing_cycle || "—"}</td>
                    <td className="px-3 py-2">${r.discounted_price ?? "—"} <span className="text-muted-foreground line-through">${r.original_price ?? ""}</span></td>
                    <td className="px-3 py-2 text-muted-foreground">{r.redeemed_at ? new Date(r.redeemed_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground" data-testid="founding40-no-redeemers">No redemptions yet.</p>
        )}
      </CardContent>
    </Card>
  );
}

function StripeVerifyButton({ type, id, authHeaders }) {
  const [state, setState] = useState("idle"); // idle | loading | valid | invalid
  const [details, setDetails] = useState(null);

  const verify = async () => {
    if (!id) return;
    setState("loading");
    try {
      const res = await axios.post(`${API}/admin/pricing/verify-stripe`, { type, id }, { headers: authHeaders });
      if (res.data.valid) {
        setState("valid");
        setDetails(res.data.details);
      } else {
        setState("invalid");
        setDetails(res.data.error);
      }
    } catch (err) {
      setState("invalid");
      setDetails(err.response?.data?.detail || "Verification failed");
    }
  };

  if (!id) return null;

  return (
    <div className="flex items-center gap-2 mt-1">
      <Button variant="ghost" size="sm" onClick={verify} disabled={state === "loading"} className="h-6 text-xs gap-1 px-2" data-testid={`verify-${type}-btn`}>
        {state === "loading" ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
        Verify
      </Button>
      {state === "valid" && (
        <span className="flex items-center gap-1 text-xs text-green-600">
          <CheckCircle className="w-3 h-3" />
          Active
          {details?.recurring?.interval ? ` (${details.recurring.interval}ly)` : ""}
        </span>
      )}
      {state === "invalid" && (
        <span className="flex items-center gap-1 text-xs text-red-600">
          <XCircle className="w-3 h-3" />
          {typeof details === "string" ? details : "Not found"}
        </span>
      )}
    </div>
  );
}

function PlanEditor({ plan, isEditing, onEdit, onSave, saving, authHeaders }) {
  const [form, setForm] = useState({ ...plan });

  useEffect(() => { setForm({ ...plan }); }, [plan]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    const updates = {};
    const fields = [
      "display_name", "description", "price_monthly", "price_annual",
      "compare_at_monthly", "compare_at_annual", "show_compare_at",
      "billing_available", "billing_default", "annual_label",
      "highlight_plan", "cta_label", "cta_type", "feature_list",
      "limitation_list", "display_order", "is_active",
      "hide_price", "price_display_text",
      "stripe_product_id", "stripe_price_id_monthly", "stripe_price_id_annual",
      "is_sellable", "promo_coupon_id",
    ];
    for (const f of fields) {
      if (JSON.stringify(form[f]) !== JSON.stringify(plan[f])) {
        updates[f] = form[f];
      }
    }
    if (Object.keys(updates).length === 0) {
      onEdit();
      return;
    }
    onSave(updates);
  };

  const hasStripeIds = plan.stripe_price_id_monthly || plan.stripe_price_id_annual;

  return (
    <Card className={`${!plan.is_active ? 'opacity-60' : ''} ${plan.highlight_plan ? 'border-primary/30' : ''}`} data-testid={`admin-plan-${plan.plan_key}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <GripVertical className="w-4 h-4 text-muted-foreground" />
            <div>
              <CardTitle className="text-base">{plan.display_name}</CardTitle>
              <p className="text-xs text-muted-foreground">{plan.plan_key} | Order: {plan.display_order}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {plan.highlight_plan && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">Highlighted</span>
            )}
            {!plan.is_active && (
              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Inactive</span>
            )}
            {plan.hide_price && (
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">Contact Sales</span>
            )}
            {hasStripeIds && (
              <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded flex items-center gap-1">
                <CreditCard className="w-3 h-3" /> Stripe
              </span>
            )}
            <span className="text-sm font-medium">
              {plan.hide_price ? (plan.price_display_text || "Custom pricing") : `$${plan.price_annual}/yr`}
            </span>
            {plan.show_compare_at && plan.compare_at_annual && (
              <span className="text-xs text-muted-foreground line-through">${plan.compare_at_annual}</span>
            )}
            <Button variant="outline" size="sm" onClick={onEdit} data-testid={`edit-plan-${plan.plan_key}`}>
              {isEditing ? "Cancel" : "Edit"}
            </Button>
          </div>
        </div>
      </CardHeader>

      {isEditing && (
        <CardContent className="space-y-6 pt-0">
          {/* Basic Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs">Display Name</Label>
              <Input value={form.display_name} onChange={(e) => set("display_name", e.target.value)} data-testid={`input-name-${plan.plan_key}`} />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={form.description} onChange={(e) => set("description", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">CTA Label</Label>
              <Input value={form.cta_label} onChange={(e) => set("cta_label", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">CTA Type</Label>
              <Select value={form.cta_type} onValueChange={(v) => set("cta_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="signup">Signup</SelectItem>
                  <SelectItem value="contact_sales">Contact Sales</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Hide Price (Contact Sales flow) */}
          <div className="border border-amber-200 rounded-lg p-4 bg-amber-50/30">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.hide_price ?? false}
                  onCheckedChange={(v) => {
                    set("hide_price", v);
                    if (v) set("is_sellable", false);
                  }}
                  data-testid={`toggle-hide-price-${plan.plan_key}`}
                />
                <Label className="text-sm font-medium">Hide price (Contact Sales)</Label>
              </div>
              {form.hide_price && (
                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded" data-testid={`hide-price-badge-${plan.plan_key}`}>
                  Checkout disabled
                </span>
              )}
            </div>
            {form.hide_price && (
              <div>
                <Label className="text-xs">Price display text</Label>
                <Input
                  value={form.price_display_text ?? ""}
                  onChange={(e) => set("price_display_text", e.target.value || null)}
                  placeholder="Custom pricing"
                  data-testid={`input-price-display-text-${plan.plan_key}`}
                />
                <p className="text-xs text-muted-foreground mt-1">Shown instead of numeric price. Defaults to "Custom pricing" if empty.</p>
              </div>
            )}
          </div>

          {/* Display Pricing */}
          <div className={form.hide_price ? "opacity-40 pointer-events-none" : ""}>
            <p className="text-sm font-medium mb-2">Display Pricing {form.hide_price && <span className="text-xs text-muted-foreground ml-2">(disabled — price is hidden)</span>}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs">Monthly Price</Label>
                <Input type="number" step="0.01" value={form.price_monthly} onChange={(e) => set("price_monthly", parseFloat(e.target.value) || 0)} data-testid={`input-monthly-${plan.plan_key}`} />
              </div>
              <div>
                <Label className="text-xs">Annual Price</Label>
                <Input type="number" step="0.01" value={form.price_annual} onChange={(e) => set("price_annual", parseFloat(e.target.value) || 0)} data-testid={`input-annual-${plan.plan_key}`} />
              </div>
              <div>
                <Label className="text-xs">Compare-At Monthly</Label>
                <Input type="number" step="0.01" value={form.compare_at_monthly ?? ""} onChange={(e) => set("compare_at_monthly", e.target.value ? parseFloat(e.target.value) : null)} />
              </div>
              <div>
                <Label className="text-xs">Compare-At Annual</Label>
                <Input type="number" step="0.01" value={form.compare_at_annual ?? ""} onChange={(e) => set("compare_at_annual", e.target.value ? parseFloat(e.target.value) : null)} />
              </div>
            </div>
          </div>

          {/* Billing (Stripe) Section */}
          <div className="border border-border rounded-lg p-4 bg-muted/10">
            <p className="text-sm font-medium mb-3 flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> Billing (Stripe)
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
              <div>
                <Label className="text-xs">Stripe Product ID</Label>
                <Input
                  value={form.stripe_product_id ?? ""}
                  onChange={(e) => set("stripe_product_id", e.target.value || null)}
                  placeholder="prod_..."
                  data-testid={`input-product-id-${plan.plan_key}`}
                />
                <StripeVerifyButton type="product" id={form.stripe_product_id} authHeaders={authHeaders} />
              </div>
              <div>
                <Label className="text-xs">Monthly Price ID</Label>
                <Input
                  value={form.stripe_price_id_monthly ?? ""}
                  onChange={(e) => set("stripe_price_id_monthly", e.target.value || null)}
                  placeholder="price_..."
                  data-testid={`input-price-monthly-${plan.plan_key}`}
                />
                <StripeVerifyButton type="price" id={form.stripe_price_id_monthly} authHeaders={authHeaders} />
              </div>
              <div>
                <Label className="text-xs">Annual Price ID</Label>
                <Input
                  value={form.stripe_price_id_annual ?? ""}
                  onChange={(e) => set("stripe_price_id_annual", e.target.value || null)}
                  placeholder="price_..."
                  data-testid={`input-price-annual-${plan.plan_key}`}
                />
                <StripeVerifyButton type="price" id={form.stripe_price_id_annual} authHeaders={authHeaders} />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Promo Coupon ID</Label>
                <Input
                  value={form.promo_coupon_id ?? ""}
                  onChange={(e) => {
                    const val = e.target.value || null;
                    set("promo_coupon_id", val);
                    set("show_compare_at", !!val);
                  }}
                  placeholder="coupon_..."
                  data-testid={`input-coupon-${plan.plan_key}`}
                />
                {form.promo_coupon_id && (
                  <StripeVerifyButton type="coupon" id={form.promo_coupon_id} authHeaders={authHeaders} />
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {form.promo_coupon_id
                    ? "Compare-at prices will show as crossed out on the pricing page."
                    : "Set a Stripe coupon to show discounted pricing to customers."}
                </p>
              </div>
              <div className="flex items-center gap-2 pt-5">
                <Switch checked={form.is_sellable} onCheckedChange={(v) => set("is_sellable", v)} data-testid={`toggle-sellable-${plan.plan_key}`} />
                <Label className="text-xs">Sellable (allows checkout)</Label>
              </div>
            </div>
          </div>

          {/* Toggles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={form.show_compare_at}
                onCheckedChange={(v) => set("show_compare_at", v)}
                disabled={!!form.promo_coupon_id}
                data-testid={`toggle-compare-${plan.plan_key}`}
              />
              <div>
                <Label className="text-xs">Show Compare-At</Label>
                {form.promo_coupon_id && (
                  <p className="text-xs text-muted-foreground">Synced with coupon</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.highlight_plan} onCheckedChange={(v) => set("highlight_plan", v)} />
              <Label className="text-xs">Highlight Plan</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} />
              <Label className="text-xs">Active</Label>
            </div>
            <div>
              <Label className="text-xs">Display Order</Label>
              <Input type="number" value={form.display_order} onChange={(e) => set("display_order", parseInt(e.target.value) || 0)} />
            </div>
          </div>

          {/* Billing Config */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Billing Available</Label>
              <Select value={form.billing_available} onValueChange={(v) => set("billing_available", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly Only</SelectItem>
                  <SelectItem value="annual">Annual Only</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Billing Default</Label>
              <Select value={form.billing_default} onValueChange={(v) => set("billing_default", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Annual Label</Label>
              <Input value={form.annual_label} onChange={(e) => set("annual_label", e.target.value)} />
            </div>
          </div>

          {/* Features */}
          <div>
            <Label className="text-xs mb-1 block">Features (one per line)</Label>
            <textarea
              className="w-full border border-border rounded-md p-3 text-sm bg-background min-h-[120px] resize-y focus:outline-none focus:ring-1 focus:ring-ring"
              value={(form.feature_list || []).join("\n")}
              onChange={(e) => set("feature_list", e.target.value.split("\n").filter(Boolean))}
              data-testid={`textarea-features-${plan.plan_key}`}
            />
          </div>

          {/* Limitations */}
          <div>
            <Label className="text-xs mb-1 block">Limitations (one per line)</Label>
            <textarea
              className="w-full border border-border rounded-md p-3 text-sm bg-background min-h-[60px] resize-y focus:outline-none focus:ring-1 focus:ring-ring"
              value={(form.limitation_list || []).join("\n")}
              onChange={(e) => set("limitation_list", e.target.value.split("\n").filter(Boolean))}
            />
          </div>

          {/* Save */}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid={`save-plan-${plan.plan_key}`}>
              <Save className="w-4 h-4" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
